import { isNil, omitBy } from 'lodash';

import {
    type GroupCollectionsCache,
    type GroupCollectionsFilterQuery,
    type GroupFilterQuery,
    type GroupFiltersCache,
    type GroupFloorPriceCache,
    type GroupItemOwnership,
    type GroupItemsFilterQuery,
    type GroupOwnershipCache,
    type GroupsCacheKey,
    type GroupsItemsFilterQuery,
    type PagingParams,
    type UseGroupsFilterParams,
} from '@app/client/common/structs';

// React Query cache keys for groups queries.
export const groupsCacheKeys = {
    groupsKey: ['groups'] as GroupsCacheKey,
    groupAll: (groupId?: string) => (groupId ? (['group', groupId] as const) : (['group'] as const)),
    group: (groupId?: string, filters?: GroupFilterQuery) =>
        ([...groupsCacheKeys.groupAll(groupId), 'base', filters] as const).filter(Boolean),

    groups: (filters?: GroupsItemsFilterQuery) => {
        const groups = [...groupsCacheKeys.groupsKey];
        if (filters?.type) {
            const filtersKey: string = filters.type;
            const type: GroupFiltersCache = { [filtersKey]: true };
            groups.push(type);
        }
        if (filters?.ownership) {
            const ownership: GroupOwnershipCache = {
                ownership: filters.ownership as GroupItemOwnership[],
            };
            groups.push(ownership);
        }
        if (filters?.min_floor_price || filters?.max_floor_price) {
            const floorPriceCache = omitBy<GroupFloorPriceCache>(
                {
                    min_floor_price: filters.min_floor_price,
                    max_floor_price: filters.max_floor_price,
                },
                isNil,
            );
            groups.push(floorPriceCache);
        }
        if (filters?.contract_addresses) {
            const collectionsCache: GroupCollectionsCache = {
                collections: filters.contract_addresses,
            };
            groups.push(collectionsCache);
        }
        return groups;
    },
    canEdit: (groupId?: string) => [...groupsCacheKeys.groupAll(groupId), 'can_edit'] as const,
    canModerate: (groupId?: string) => [...groupsCacheKeys.members(groupId), 'can_moderate'] as const,
    items: (groupId?: string, filters?: GroupItemsFilterQuery) =>
        ([...groupsCacheKeys.groupAll(groupId), 'items', filters] as const).filter(Boolean),
    invites: (groupId?: string) => [...groupsCacheKeys.groupAll(groupId), 'invites', 'members'] as const,
    members: (groupId?: string) => [...groupsCacheKeys.groupAll(groupId), 'members'] as const,
    followers: (groupId?: string) => [...groupsCacheKeys.groupAll(groupId), 'followers'] as const,
    collections: (groupId?: string, filters?: GroupCollectionsFilterQuery) =>
        ([...groupsCacheKeys.groupAll(groupId), 'collections', filters] as const).filter(Boolean),
    groupsCollections: (filters?: Partial<UseGroupsFilterParams>) =>
        ([...groupsCacheKeys.groupsKey, 'collections', filters] as const).filter(Boolean),
    showInvite: (inviteId?: string) => [...groupsCacheKeys.groupAll(), 'invites', inviteId] as const,
    inviteFreeYat: (inviteId?: string) => [...groupsCacheKeys.groupAll(), 'invites', inviteId, 'free-yat'] as const,
};

export const usersCacheKeys = {
    users: (primaryYat?: string, twitterHandle?: string) => {
        return ['users', ...(primaryYat ? [primaryYat] : []), ...(twitterHandle ? [twitterHandle] : [])];
    },
    inviteData: ['invite-data'] as const,
};

type PageParam = {
    paging: PagingParams;
    data: unknown;
};
export const getNextPageParam = <T>(lastPage: PageParam & T, pages: (PageParam & T)[]) => {
    if (lastPage && lastPage?.paging?.total && lastPage?.paging?.limit && lastPage?.paging?.page != null) {
        const total = lastPage?.paging?.total || 0;
        const limit = lastPage?.paging?.limit || 1;
        const totalPages = Math.ceil(total / limit);

        if (pages?.length < totalPages) {
            return pages?.length;
        } else {
            return undefined;
        }
    }
    return undefined;
};

export const getPagesContent = <T>(pages?: (PageParam & T)[]) =>
    (pages?.map((page) => page?.data).flat() ?? []) as unknown as (PageParam & T)['data'];

export const getPagesPagingParams = <T>(pages?: (PageParam & T)[]) => {
    const paging = pages?.map((page) => page?.paging)?.flat();
    return paging?.[paging?.length - 1 || 0] || paging?.[0] || ({ page: 0, limit: 20, total: 0 } as PagingParams);
};
// TODO remove below if can
// todo - check if right approach?
export const getPagesPaging = <T>(pages?: (PageParam & T)[]): PagingParams =>
    pages?.find((page) => page.paging)?.paging ?? { page: 0, limit: 20, total: 0 };
